// src/hooks/useFetchSearchResults.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  SEARCH_PAGE_ALL_STUDIOS,
  SEARCH_PAGE_ALL_STUDIOS_UNAUTH
} from '../../graphql/queries/searchQueries';
import debounce from 'lodash/debounce';
import { Loader } from '@googlemaps/js-api-loader';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';
import { studioHasTagValue } from '../../util/tagsHelper';
import { filterPublicStudios } from '../../util/studioFilters';
import {
  SEARCH_FETCH_GUARD_REASONS,
  SEARCH_LOCATION_MODES,
  buildSearchBounds,
  dedupeStudiosById,
  filterAndSortStudiosByDistance,
  getSearchFetchGuard,
  normalizeSearchLocation,
} from '../../util/searchGeo';

import { useHasStudios } from '../useHasStudios';

import awsConfig from '../../config/aws-config';
import { useSelector } from "react-redux";

/** Main listing query: react quickly to URL/search changes without hammering the API. */
const DEFAULT_SEARCH_DEBOUNCE_MS = 320;
const SEARCH_PAGE_SIZE = 100;

/**
 * Secondary query (e.g. full price range for filters): delay so it does not compete with the listing request.
 */
export const SECONDARY_SEARCH_DEBOUNCE_MS = 720;

/**
 * Custom hook for fetching studio search results
 *
 * @param {Object} searchParams - Object containing search parameters
 * @param {string} [searchParams.location] - Location label from the URL/search input
 * @param {string} [searchParams.locationPlaceId] - Stable Google Place ID when selected from suggestions
 * @param {number} [searchParams.latitude] - Center latitude for search
 * @param {number} [searchParams.longitude] - Center longitude for search
 * @param {number} [searchParams.radius] - Search radius in kilometers
 * @param {number} [searchParams.selectedGuest] - Number of guests
 * @param {string} [searchParams.studioTag] - Studio tag name
 * @param {Object} [options]
 * @param {number} [options.debounceMs] - Delay before firing the GraphQL request (default: primary 320ms; pass higher for secondary fetches).
 * @param {boolean} [options.skip] - When true, no request is scheduled (e.g. defer work until another condition is met).
 */
export const useFetchSearchResults = (searchParams, options = {}) => {
  const { debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS, skip = false } = options;
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [allStudios, setAllStudios] = useState([]);
  const [activeBounds, setActiveBounds] = useState(null);
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [placeResolutionError, setPlaceResolutionError] = useState(null);
  const requestIdRef = useRef(0);
  const { id: userId } = useSelector((state) => state?.auth?.user) || {};

  const { hasStudios, loading: hasStudiosLoading } = useHasStudios(userId);

  const locationIntent = useMemo(
    () => normalizeSearchLocation({
      location: searchParams.location,
      locationPlaceId: searchParams.locationPlaceId,
    }),
    [searchParams.location, searchParams.locationPlaceId]
  );

  const isPlaceSearch = locationIntent.mode === SEARCH_LOCATION_MODES.PLACE;
  const knownLocationCenter = locationIntent.center || null;

  const searchCenter = useMemo(() => {
    if (!activeBounds) return null;
    return {
      lat: activeBounds.centerLatitude,
      lng: activeBounds.centerLongitude,
    };
  }, [activeBounds]);

  // Initialize Google Maps API only for concrete place searches.
  useEffect(() => {
    if (knownLocationCenter) {
      setIsApiLoaded(false);
      return undefined;
    }

    if (!isPlaceSearch) {
      setIsApiLoaded(false);
      return undefined;
    }

    let cancelled = false;

    const loadGoogleMapsAPI = async () => {
      try {
        if (window.google && window.google.maps && window.google.maps.places) {
          if (!cancelled) setIsApiLoaded(true);
          return;
        }

        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
          version: 'weekly',
          libraries: ['places']
        });

        await loader.load();

        if (!cancelled && window.google && window.google.maps && window.google.maps.places) {
          setIsApiLoaded(true);
        }
      } catch (loadError) {
        if (!cancelled) {
          setPlaceResolutionError(loadError);
          setError(loadError);
          setLoading(false);
        }
      }
    };

    loadGoogleMapsAPI();

    return () => {
      cancelled = true;
    };
  }, [isPlaceSearch, knownLocationCenter]);

  useEffect(() => {
    if (isPlaceSearch && knownLocationCenter) {
      setSelectedLocation({
        id: locationIntent.placeId,
        name: locationIntent.label,
        latitude: knownLocationCenter.latitude,
        longitude: knownLocationCenter.longitude,
        address: locationIntent.label,
      });
      setActiveBounds(null);
      setIsResolvingLocation(false);
      setPlaceResolutionError(null);
      return;
    }

    setSelectedLocation(null);
    setActiveBounds(null);
    setPlaceResolutionError(null);
    setIsResolvingLocation(isPlaceSearch && isApiLoaded);
  }, [
    isPlaceSearch,
    isApiLoaded,
    locationIntent.label,
    locationIntent.placeId,
    knownLocationCenter,
  ]);

  const hasGroupSize = !!searchParams.selectedGuest;

  const [fetchAllStudios, { fetchMore: fetchMoreAllStudios }] = useLazyQuery(SEARCH_PAGE_ALL_STUDIOS, {
    fetchPolicy: 'network-only',
  });

  const [fetchAllStudiosUnauth, { fetchMore: fetchMoreAllStudiosUnauth }] = useLazyQuery(SEARCH_PAGE_ALL_STUDIOS_UNAUTH, {
    fetchPolicy: 'network-only',
  });

  const getLocationCoordinates = useCallback((placeId) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return Promise.reject(new Error('Places service not initialized'));
    }

    return new Promise((resolve, reject) => {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      placesService.getDetails(
        { placeId, fields: ['geometry', 'formatted_address', 'name'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            resolve({
              id: placeId,
              name: place.formatted_address || place.name,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              address: place.formatted_address
            });
          } else {
            reject(new Error(`Failed to get location details: ${status}`));
          }
        }
      );
    });
  }, []);

  const getPlaceIdFromText = useCallback((locationText) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return Promise.reject(new Error('Places service not initialized'));
    }

    return new Promise((resolve, reject) => {
      const autocompleteService = new window.google.maps.places.AutocompleteService();

      autocompleteService.getPlacePredictions(
        {
          input: locationText,
          componentRestrictions: { country: 'de' },
          types: ['geocode', 'establishment']
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
            resolve(predictions[0].place_id);
          } else {
            reject(new Error(`Autocomplete failed with status: ${status}`));
          }
        }
      );
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchLocationCoordinates = async () => {
      if (!isPlaceSearch) {
        setSelectedLocation(null);
        setIsResolvingLocation(false);
        return;
      }

      if (knownLocationCenter) {
        return;
      }

      if (!isApiLoaded) {
        return;
      }

      setIsResolvingLocation(true);

      try {
        const placeId = locationIntent.placeId || await getPlaceIdFromText(locationIntent.label);
        const locationData = await getLocationCoordinates(placeId);

        if (!cancelled) {
          setSelectedLocation(locationData);
          setPlaceResolutionError(null);
          setError(null);
        }
      } catch (resolveError) {
        if (!cancelled) {
          setSelectedLocation(null);
          setPlaceResolutionError(resolveError);
          setError(resolveError);
          setLoading(false);
        }
      } finally {
        if (!cancelled) {
          setIsResolvingLocation(false);
        }
      }
    };

    fetchLocationCoordinates();

    return () => {
      cancelled = true;
    };
  }, [
    isPlaceSearch,
    isApiLoaded,
    locationIntent.label,
    locationIntent.placeId,
    knownLocationCenter,
    getPlaceIdFromText,
    getLocationCoordinates,
  ]);

  const calculateSearchBounds = useCallback(() => {
    if (!isPlaceSearch && searchParams.latitude == null && searchParams.longitude == null) {
      return null;
    }

    const latitude = selectedLocation?.latitude ?? searchParams.latitude;
    const longitude = selectedLocation?.longitude ?? searchParams.longitude;
    const radiusKm = searchParams.radius ?? 30;

    return buildSearchBounds({ latitude, longitude, radiusKm });
  }, [isPlaceSearch, searchParams.latitude, searchParams.longitude, searchParams.radius, selectedLocation]);

  const fetchAllStudioPages = useCallback(async ({ execute, fetchMore, requestId }) => {
    let nextToken = null;
    let pageResult = await execute({
      variables: {
        limit: SEARCH_PAGE_SIZE,
        nextToken,
      },
    });

    const items = [];

    while (pageResult?.data?.listStudios && requestId === requestIdRef.current) {
      const connection = pageResult.data.listStudios;
      items.push(...(connection.items || []));
      nextToken = connection.nextToken || null;

      if (!nextToken) break;

      if (typeof fetchMore === 'function') {
        pageResult = await fetchMore({
          variables: {
            limit: SEARCH_PAGE_SIZE,
            nextToken,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => fetchMoreResult || previousResult,
        });
      } else {
        pageResult = await execute({
          variables: {
            limit: SEARCH_PAGE_SIZE,
            nextToken,
          },
        });
      }
    }

    if (requestId !== requestIdRef.current) {
      return null;
    }

    return dedupeStudiosById(items);
  }, []);

  const processStudioItems = useCallback((items, bounds) => {
    let processedStudios = (items || []).map(studio => {
      if (studio.images && Array.isArray(studio.images)) {
        const processedImages = studio.images.map(imageKey => {
          if (!imageKey) return '';

          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 600,
              height: 450,
              fit: 'cover'
            });
          } catch (imageError) {
            try {
              const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
              return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
            } catch (fallbackError) {
              return '';
            }
          }
        });

        return {
          ...studio,
          processedImages,
          originalImages: [...studio.images],
          hasImages: processedImages.length > 0
        };
      }

      return {
        ...studio,
        processedImages: [],
        originalImages: [],
        hasImages: false
      };
    });

    if (searchParams.studioTag) {
      processedStudios = processedStudios.filter(studio =>
        studioHasTagValue(studio, searchParams.studioTag)
      );
    }

    if (hasGroupSize && searchParams.selectedGuest) {
      const requiredPeople = parseInt(searchParams.selectedGuest, 10);
      processedStudios = processedStudios.filter(studio =>
        studio.numberOfPeople >= requiredPeople
      );
    }

    let publicStudios = filterPublicStudios(processedStudios);

    if (bounds) {
      publicStudios = filterAndSortStudiosByDistance(publicStudios, bounds);
    }

    return publicStudios;
  }, [hasGroupSize, searchParams.selectedGuest, searchParams.studioTag]);

  const performFetch = useCallback(async (requestId) => {
    const fetchGuard = getSearchFetchGuard({
      skip,
      isResolvingLocation,
      isPlaceSearch,
      selectedLocation,
      locationResolveFailed: Boolean(placeResolutionError),
    });

    if (fetchGuard) {
      if (fetchGuard === SEARCH_FETCH_GUARD_REASONS.UNRESOLVABLE_LOCATION) {
        setAllStudios([]);
        setStudios([]);
        setActiveBounds(null);
        setLoading(false);
      }
      return;
    }

    const bounds = calculateSearchBounds();
    const isUnauthenticated = !userId;
    const execute = isUnauthenticated ? fetchAllStudiosUnauth : fetchAllStudios;
    const fetchMore = isUnauthenticated ? fetchMoreAllStudiosUnauth : fetchMoreAllStudios;

    setActiveBounds(bounds);
    setLoading(true);
    setError(null);

    try {
      const rawStudios = await fetchAllStudioPages({ execute, fetchMore, requestId });
      if (!rawStudios || requestId !== requestIdRef.current) return;

      const publicStudios = processStudioItems(rawStudios, bounds);

      if (requestId !== requestIdRef.current) return;

      setAllStudios(publicStudios);
      setStudios(publicStudios);
      setLoading(false);
    } catch (fetchError) {
      if (requestId !== requestIdRef.current) return;
      setError(fetchError);
      setLoading(false);
    }
  }, [
    skip,
    isResolvingLocation,
    isPlaceSearch,
    selectedLocation,
    placeResolutionError,
    calculateSearchBounds,
    userId,
    fetchAllStudiosUnauth,
    fetchAllStudios,
    fetchMoreAllStudiosUnauth,
    fetchMoreAllStudios,
    fetchAllStudioPages,
    processStudioItems,
  ]);

  const debouncedFetch = useMemo(
    () => debounce((requestId) => performFetch(requestId), debounceMs),
    [debounceMs, performFetch]
  );

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    const fetchGuard = getSearchFetchGuard({
      skip,
      isResolvingLocation,
      isPlaceSearch,
      selectedLocation,
      locationResolveFailed: Boolean(placeResolutionError),
    });

    if (fetchGuard === SEARCH_FETCH_GUARD_REASONS.SKIPPED) {
      debouncedFetch.cancel();
      setLoading(false);
      return undefined;
    }

    setAllStudios([]);
    setStudios([]);

    if (fetchGuard === SEARCH_FETCH_GUARD_REASONS.UNRESOLVABLE_LOCATION) {
      debouncedFetch.cancel();
      setActiveBounds(null);
      setLoading(false);
      if (placeResolutionError) setError(placeResolutionError);
      return undefined;
    }

    if (fetchGuard === SEARCH_FETCH_GUARD_REASONS.WAITING_FOR_LOCATION) {
      debouncedFetch.cancel();
      setActiveBounds(null);
      setLoading(true);
      return undefined;
    }

    setLoading(true);
    debouncedFetch(requestId);

    return () => debouncedFetch.cancel();
  }, [
    skip,
    debouncedFetch,
    searchParams.key,
    searchParams.latitude,
    searchParams.longitude,
    searchParams.radius,
    searchParams.studioTag,
    searchParams.selectedGuest,
    selectedLocation,
    placeResolutionError,
    isResolvingLocation,
    isPlaceSearch,
    isApiLoaded,
    userId,
  ]);

  const refetchWithParams = useCallback(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    performFetch(requestId);
  }, [performFetch]);

  const isLimitedUser = !hasStudiosLoading && (!userId || hasStudios === false);

  return {
    studios,
    loading,
    error,
    refetch: refetchWithParams,
    selectedLocation,
    searchCenter,
    allStudios,
    isLimitedUser,
    totalStudiosCount: allStudios.length,
  };
};

export default useFetchSearchResults;
